// Copyright 1999-2024. WebPros International GmbH. All rights reserved.

import {
    IAdditionalOptions,
    ISelectOption,
} from 'common/components/SelectInput';
import {
    GroupType,
    ValueType,
} from 'react-select';
import * as React from 'react';
import { Dialog } from 'common/components/Dialog/Dialog';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Paragraph } from 'common/components/ServerTabs/RescueTab/Styles';
import { Button } from 'admin/common/components/Button/Button';
import AsyncSelectInput,
{ IAsyncSelectInputProps } from 'common/components/Select/AsyncSelectInput';

export interface IAsyncSelectWithConfirmationProps extends
    IAsyncSelectInputProps<ISelectOption, GroupType<ISelectOption>, IAdditionalOptions, false> {
        confirmDialog: {
            title: React.ReactNode;
            text: React.ReactNode;
            button: React.ReactNode;
        };
        resetDialog?: {
            title: React.ReactNode;
            text: React.ReactNode;
            button: React.ReactNode;
        };
        onChange: (value: ValueType<ISelectOption>) => void;
}

export const AsyncSelectWithConfirmation: React.FC<IAsyncSelectWithConfirmationProps> = ({
    confirmDialog,
    resetDialog,
    onChange,
    ...asyncSelectInputProps
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [valueForConfirm, setValueForConfirm] = React.useState<ValueType<ISelectOption>>(null);

    const handleDialogClose = () => setIsOpen(false);
    const handleSelectChange = (option: ValueType<ISelectOption>): void => {
        setValueForConfirm(option);
        setIsOpen(true);
    };
    const handleButtonClick = () => {
        setIsOpen(false);
        onChange(valueForConfirm);
    };

    const dialog = valueForConfirm === null && resetDialog ? resetDialog : confirmDialog;

    return (
        <>
            <AsyncSelectInput {...asyncSelectInputProps}
                onChange={handleSelectChange}
            />
            <Dialog
                heading={dialog.title}
                closeHandler={handleDialogClose}
                isOpen={isOpen}
                size={SIZE.XS}
            >
                <Paragraph>{dialog.text}</Paragraph>
                <Button
                    fill={true}
                    onClick={handleButtonClick}
                    size={SIZE.LG}
                    intent={INTENT_TYPE.PRIMARY}
                >
                    {dialog.button}
                </Button>
            </Dialog>
        </>
    );
};

export default AsyncSelectWithConfirmation;
